import { ChangePrimaryPanel } from './change-primary-panel';
import { ChangeTargetPanel } from './change-target-panel';
import { ReasonPanel } from './reason-panel';
import type { AccountAction } from './account-email-actions.hooks';
import type { ManagedUser } from './types';

interface AccountActionPanelProps {
  action: AccountAction;
  user: ManagedUser;
  busy: boolean;
  targetError: string | null;
  onPreview: (args: { email: string }) => void;
  onApplyPrimary: (args: { email: string; keepOldAsSecondary: boolean; reason: string }) => void;
  onAdd: (args: { email: string; reason: string }) => void;
  onRemove: (args: { email: string; reason: string }) => void;
  onReleaseAdd: (args: { email: string; holder: ManagedUser; reason: string }) => void;
  onCancel: () => void;
}

/**
 * The panel for the action open on an account card: pick a new primary, review the preview, or
 * confirm an add, remove or release with a reason.
 */
export function AccountActionPanel({
  action,
  user,
  busy,
  targetError,
  onPreview,
  onApplyPrimary,
  onAdd,
  onRemove,
  onReleaseAdd,
  onCancel,
}: AccountActionPanelProps) {
  switch (action.type) {
    case 'change-target':
      return (
        <ChangeTargetPanel
          currentEmail={user.email}
          loading={busy}
          serverError={targetError}
          onSubmit={onPreview}
          onCancel={onCancel}
        />
      );
    case 'preview':
      return (
        <ChangePrimaryPanel
          oldEmail={user.email}
          newEmail={action.email}
          isNewAddress={!user.secondaryEmails.some(secondary => secondary.email === action.email)}
          counts={action.counts}
          submitting={busy}
          onSubmit={args => onApplyPrimary({ email: action.email, ...args })}
          onCancel={onCancel}
        />
      );
    case 'add':
      return (
        <ReasonPanel
          title={`Add ${action.email}`}
          description="The address is added to this account as a secondary email."
          submitLabel="Add email"
          submitting={busy}
          onSubmit={({ reason }) => onAdd({ email: action.email, reason })}
          onCancel={onCancel}
        />
      );
    case 'remove':
      return (
        <ReasonPanel
          title={`${user.isDeleted ? 'Release' : 'Remove'} ${action.email}`}
          description={
            user.isDeleted
              ? 'The address is taken off this deleted account so another account can use it.'
              : 'The user can no longer sign in with this address.'
          }
          submitLabel={user.isDeleted ? 'Release email' : 'Remove email'}
          destructive
          submitting={busy}
          onSubmit={({ reason }) => onRemove({ email: action.email, reason })}
          onCancel={onCancel}
        />
      );
    case 'release-add':
      return (
        <ReasonPanel
          title={`Move ${action.email} to this account`}
          description={`Releases the address from the deleted account (${action.holder.id}) and adds it here as a secondary email.`}
          submitLabel="Release and add"
          submitting={busy}
          onSubmit={({ reason }) =>
            onReleaseAdd({ email: action.email, holder: action.holder, reason })
          }
          onCancel={onCancel}
        />
      );
  }
}
